import React, { useState } from 'react';
import { Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PresetFilterCard } from './PresetFilterCard';
import { PresetFilter, FilterValue, FilterRecord } from '@/types/filterBuilder';

interface PresetCategory {
  id: string;
  title: string;
  presets: PresetFilter[];
}

interface CommonFlowsPanelProps {
  presetCategories: PresetCategory[];
  onApplyFilters: (filters: FilterRecord) => void;
  onClearFilters?: () => void;
}

export function CommonFlowsPanel({
  presetCategories,
  onApplyFilters,
  onClearFilters,
}: CommonFlowsPanelProps) {
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [configurableValues, setConfigurableValues] = useState<Record<string, FilterValue>>({});

  const selectedPreset = presetCategories
    .flatMap((c) => c.presets)
    .find((p) => p.id === selectedPresetId);

  const handlePresetSelect = (preset: PresetFilter) => {
    const hasConfigurableFields = preset.configurableFields && preset.configurableFields.length > 0;

    if (hasConfigurableFields) {
      // Toggle selection for configurable presets
      if (selectedPresetId === preset.id) {
        setSelectedPresetId(null);
        setConfigurableValues({});
      } else {
        setSelectedPresetId(preset.id);
        setConfigurableValues({});
      }
    } else {
      // Immediately apply for presets without configurable fields
      onApplyFilters(preset.filters);
      setSelectedPresetId(null);
      setConfigurableValues({});
    }
  };

  const handleConfigurableChange = (key: string, value: FilterValue) => {
    setConfigurableValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApplyConfigurable = () => {
    if (!selectedPreset) return;

    // Build the final filters object
    const filters: FilterRecord = { ...selectedPreset.filters };

    // Add configurable field values
    if (selectedPreset.configurableFields) {
      for (const field of selectedPreset.configurableFields) {
        const value = configurableValues[field.key];
        if (value !== null && value !== undefined) {
          // Handle range types
          if (field.inputType === 'date-range' && field.rangeKeys) {
            const rangeValue = value as { start?: string; end?: string };
            if (rangeValue.start) {
              filters[field.rangeKeys.start] = rangeValue.start;
            }
            if (rangeValue.end) {
              filters[field.rangeKeys.end] = rangeValue.end;
            }
          } else if (field.inputType === 'number-range' && field.rangeKeys) {
            const rangeValue = value as { min?: number; max?: number };
            if (rangeValue.min !== undefined) {
              filters[field.rangeKeys.start] = rangeValue.min;
            }
            if (rangeValue.max !== undefined) {
              filters[field.rangeKeys.end] = rangeValue.max;
            }
          } else if (field.inputType === 'multi-select') {
            // Join array values with comma
            filters[field.key] = (value as string[]).join(',');
          } else {
            filters[field.key] = value;
          }
        }
      }
    }

    onApplyFilters(filters);
    setSelectedPresetId(null);
    setConfigurableValues({});
  };

  const canApplyConfigurable = () => {
    if (!selectedPreset) return false;

    // Check if all required configurable fields have values
    if (selectedPreset.configurableFields) {
      for (const field of selectedPreset.configurableFields) {
        if (field.required) {
          const value = configurableValues[field.key];
          if (value === null || value === undefined) return false;
          if (Array.isArray(value) && value.length === 0) return false;
          if (typeof value === 'object' && !Array.isArray(value)) {
            // For range types, check if at least one value is set
            const rangeValue = value as Record<string, any>;
            const hasValue = Object.values(rangeValue).some(
              (v) => v !== undefined && v !== null && v !== ''
            );
            if (!hasValue) return false;
          }
        }
      }
    }

    return true;
  };

  return (
    <div className="flex flex-col">
      {/* All Patients button at top */}
      <div className="mb-4">
        <Button
          variant="outline"
          onClick={() => onClearFilters?.()}
          className="flex items-center gap-2"
        >
          <Users className="h-4 w-4" />
          All Patients
        </Button>
      </div>

      <div className="max-h-[55vh] overflow-y-auto space-y-6 pr-2">
        {presetCategories.map((category) => (
          <div key={category.id} className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">
              {category.title}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {category.presets.map((preset) => (
                <PresetFilterCard
                  key={preset.id}
                  preset={preset}
                  isSelected={selectedPresetId === preset.id}
                  onSelect={() => handlePresetSelect(preset)}
                  configurableValues={configurableValues}
                  onConfigurableChange={handleConfigurableChange}
                  onApply={handleApplyConfigurable}
                  canApply={canApplyConfigurable()}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default CommonFlowsPanel;
